import type { QueryClient } from '@tanstack/react-query'
import type { FolderNode } from '../../domains/dashboard/models'

type ArchivedFolderLike = Pick<FolderNode, 'archived' | 'name' | 'parentId'> | null | undefined

export const isSystemArchivedContainer = (folder: ArchivedFolderLike): boolean => {
  if (!folder) return false
  const parentId = folder.parentId ?? null
  return (folder.name ?? '').trim().toLowerCase() === 'archived' && parentId == null
}

export const isArchivedFolderView = (folder: ArchivedFolderLike): boolean => {
  if (!folder) return false
  return folder.archived === true || isSystemArchivedContainer(folder)
}

export const isUserArchivedFolderView = (folder: ArchivedFolderLike): boolean => {
  if (!folder) return false
  return isArchivedFolderView(folder) && !isSystemArchivedContainer(folder)
}

/** Diagram-level restore/delete is only for items stored in the system Archived bucket. */
export const allowsDiagramLevelArchiveActions = (folder: ArchivedFolderLike): boolean => {
  return isSystemArchivedContainer(folder)
}

/** Prefer the folder API's archive flag over a stale overview-tree node. */
export const mergeFolderArchiveFromApi = <T extends FolderNode>(
  folder: T,
  selectedFolderId: string | null | undefined,
  apiFolder: (Pick<FolderNode, 'id' | 'archived'> & Partial<T>) | null | undefined,
): T => {
  if (!apiFolder || apiFolder.id !== selectedFolderId || apiFolder.archived === undefined) {
    return folder
  }
  return { ...folder, archived: apiFolder.archived }
}

const isDiagramListedInArchivedTree = (
  diagramId: string,
  archivedRoots: readonly FolderNode[],
): boolean => {
  const visit = (roots: readonly FolderNode[]): boolean => {
    for (const node of roots) {
      if (node.diagrams?.some((diagram) => diagram.id === diagramId)) return true
      if (node.children?.length && visit(node.children)) return true
    }
    return false
  }
  return visit(archivedRoots)
}

/**
 * Whether a diagram should render as archived/read-only in the folder view.
 * When the folder API reports a normal folder is active again, ignore stale
 * entries still present in the dashboard overview's archived tree until
 * refetch completes. Never override an archived folder view (including the
 * system Archived bucket, which is not flagged archived=true on the API).
 */
export const isDiagramArchivedForDashboard = (input: {
  diagramId: string
  archivedRoots: readonly FolderNode[]
  selectedFolderId: string | null
  folderArchivedFromApi: boolean | undefined
  resolvedFolderArchived: boolean
}): boolean => {
  const {
    diagramId,
    archivedRoots,
    selectedFolderId,
    folderArchivedFromApi,
    resolvedFolderArchived,
  } = input

  if (resolvedFolderArchived || folderArchivedFromApi === true) return true
  if (selectedFolderId && folderArchivedFromApi === false) return false
  return isDiagramListedInArchivedTree(diagramId, archivedRoots)
}

/** Keep folder detail queries aligned after archive/restore mutations. */
export const syncFolderArchiveCache = async (
  queryClient: QueryClient,
  folderId: string,
  archived: boolean,
): Promise<void> => {
  queryClient.setQueryData<{ folder?: { archived?: boolean } }>(
    ['folder', folderId, 'data'],
    (current) => {
      if (!current?.folder) return current
      return { ...current, folder: { ...current.folder, archived } }
    },
  )
  queryClient.setQueryData(['folder', folderId, 'archived'], archived)
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
    queryClient.invalidateQueries({ queryKey: ['folder', folderId] }),
  ])
}

/** Mute archived diagram preview/text without dimming sibling action controls. */
export const archivedDiagramMutedSurfaceClass = 'opacity-60 pointer-events-none'
